import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { 
  Mic, Play, Pause, Square, ArrowLeft, 
  Music, Loader2, AudioWaveform,
  Share2, Copy, Check, Users, TrendingUp,
  SkipBack, SkipForward, CircleStop, Circle,
  Volume2, Settings, Drum, Activity, Sparkles, Key, ExternalLink, 
  CheckCircle, XCircle, Plus, Trash2, Volume1, VolumeX, Grid3X3, Wand2, RefreshCw,
  Download, FileAudio, FileMusic
} from 'lucide-react';
import { toast } from 'sonner';
import * as Tone from 'tone';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Generate unique ID
const generateId = () => Math.random().toString(36).substr(2, 9);

// Batucada instrument colors
const INSTRUMENT_COLORS = {
  surdo: { bg: 'bg-red-500', text: 'text-red-500', hex: '#ef4444' },
  caixa: { bg: 'bg-blue-500', text: 'text-blue-500', hex: '#3b82f6' },
  tamborim: { bg: 'bg-yellow-500', text: 'text-yellow-500', hex: '#eab308' },
  repinique: { bg: 'bg-green-500', text: 'text-green-500', hex: '#22c55e' },
  agogo: { bg: 'bg-purple-500', text: 'text-purple-500', hex: '#a855f7' }
};

// Track colors for visual distinction
const TRACK_COLORS = [
  { bg: 'bg-amber-500/20', border: 'border-amber-500/50', wave: '#f59e0b', name: 'Amber' },
  { bg: 'bg-emerald-500/20', border: 'border-emerald-500/50', wave: '#10b981', name: 'Emerald' },
  { bg: 'bg-purple-500/20', border: 'border-purple-500/50', wave: '#a855f7', name: 'Purple' },
  { bg: 'bg-blue-500/20', border: 'border-blue-500/50', wave: '#3b82f6', name: 'Blue' },
  { bg: 'bg-pink-500/20', border: 'border-pink-500/50', wave: '#ec4899', name: 'Pink' },
  { bg: 'bg-cyan-500/20', border: 'border-cyan-500/50', wave: '#06b6d4', name: 'Cyan' },
];

export default function HumToMusic() {
  const [searchParams] = useSearchParams();
  
  // Recording mode - BEAT (beatbox to drums) or MELODY (hum to notes)
  const [recordingMode, setRecordingMode] = useState('beat'); // 'beat' or 'melody'
  
  // Multi-track state - now includes MIDI events
  const [tracks, setTracks] = useState([
    { 
      id: generateId(), 
      name: 'Track 1', 
      audioBlob: null,       // Original recording
      waveformData: null,    // For display
      midiEvents: [],        // Detected MIDI events
      pattern: [],           // Step pattern
      volume: 0.8, 
      muted: false, 
      solo: false, 
      duration: 0,
      isConverted: false,    // Has been converted to MIDI/samples
      mode: 'beat',          // Track's recording mode
      currentLevel: 0        // Real-time playback level for meter
    }
  ]);
  const [selectedTrackId, setSelectedTrackId] = useState(null);
  const [activeTrackId, setActiveTrackId] = useState(null);
  
  // Sample players
  const [samplesLoaded, setSamplesLoaded] = useState(false);
  const [loadingSamples, setLoadingSamples] = useState(false);
  const samplePlayersRef = useRef({});
  
  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isConverting, setIsConverting] = useState(false);
  
  // Playback state
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackPosition, setPlaybackPosition] = useState(0);
  const [bpm, setBpm] = useState(120);
  
  // Metronome state
  const [metronomeEnabled, setMetronomeEnabled] = useState(false);
  const [metronomeCount, setMetronomeCount] = useState(0);
  const [isCountingIn, setIsCountingIn] = useState(false);
  
  // Quantize state
  const [quantizeEnabled, setQuantizeEnabled] = useState(true);
  const [gridSize, setGridSize] = useState(16); // 1/16 notes default
  
  // Input level state
  const [inputLevel, setInputLevel] = useState(0);
  const [peakLevel, setPeakLevel] = useState(0);
  const [inputGain, setInputGain] = useState(1.0); // Input gain control
  
  // Track level meters (updated during playback)
  const [trackLevels, setTrackLevels] = useState({});
  
  // Microphone permission state
  const [micPermission, setMicPermission] = useState('pending'); // 'pending', 'granted', 'denied'
  
  // Settings & AI
  const [showSettings, setShowSettings] = useState(false);
  const [replicateKey, setReplicateKey] = useState(() => localStorage.getItem('replicate_api_key') || '');
  const [isKeyValid, setIsKeyValid] = useState(null);
  const [isValidatingKey, setIsValidatingKey] = useState(false);
  
  // Share state
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareTitle, setShareTitle] = useState('');
  const [creatorName, setCreatorName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [copied, setCopied] = useState(false);
  
  // Community
  const [showCommunityBeats, setShowCommunityBeats] = useState(false);
  const [communityBeats, setCommunityBeats] = useState([]);
  const [loadingBeats, setLoadingBeats] = useState(false);
  
  // Refs
  const waveformCanvasRefs = useRef({});
  const levelCanvasRef = useRef(null);
  const analyserRef = useRef(null);
  const levelAnimationRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioContextRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const metronomeRef = useRef(null);
  const metronomeIntervalRef = useRef(null);
  const audioPlayersRef = useRef({});
  const playbackIntervalRef = useRef(null);
  const gainNodesRef = useRef({});
  const recordingTrackIdRef = useRef(null); // Store track ID being recorded to
  const micStreamRef = useRef(null); // Store pre-authorized mic stream
  const sequenceRef = useRef(null); // For Tone.js sequence playback
  const melodySynthRef = useRef(null); // For melody playback
  const audioAnalysersRef = useRef({}); // For real-time level metering
  const tonePlayersRef = useRef({}); // Tone.js players for proper metering
  const toneMetersRef = useRef({}); // Tone.Meter instances for each track

  // Load drum samples - called on first user interaction
  const loadSamples = async () => {
    if (samplesLoaded || loadingSamples) return;
    
    setLoadingSamples(true);
    try {
      // Get available samples from backend
      const response = await axios.get(`${API}/api/ai-audio/samples`);
      const sampleUrls = response.data.samples;
      
      // Start Tone.js (requires user gesture)
      await Tone.start();
      
      // Load each sample into Tone.js
      const loadPromises = [];
      for (const [instrument, info] of Object.entries(sampleUrls)) {
        try {
          const player = new Tone.Player({
            url: `${API}${info.url}`,
          }).toDestination();
          samplePlayersRef.current[instrument] = player;
          loadPromises.push(new Promise(resolve => {
            player.buffer.onload = () => {
              console.log(`Loaded ${instrument} sample`);
              resolve();
            };
          }));
        } catch (err) {
          console.error(`Failed to load ${instrument}:`, err);
        }
      }
      
      // Wait for all samples to load (with timeout)
      await Promise.race([
        Promise.all(loadPromises),
        new Promise(resolve => setTimeout(resolve, 5000))
      ]);
      
      setSamplesLoaded(true);
      toast.success('Drum samples loaded!', { duration: 2000 });
    } catch (err) {
      console.error('Failed to load samples:', err);
      toast.error('Could not load drum samples');
    } finally {
      setLoadingSamples(false);
    }
  };

  // Trigger sample loading on first user interaction
  useEffect(() => {
    const handleFirstInteraction = () => {
      if (!samplesLoaded && !loadingSamples) {
        loadSamples();
      }
      // Remove listeners after first interaction
      document.removeEventListener('click', handleFirstInteraction);
      document.removeEventListener('keydown', handleFirstInteraction);
    };
    
    document.addEventListener('click', handleFirstInteraction);
    document.addEventListener('keydown', handleFirstInteraction);
    
    return () => {
      document.removeEventListener('click', handleFirstInteraction);
      document.removeEventListener('keydown', handleFirstInteraction);
      // Dispose sample players
      Object.values(samplePlayersRef.current).forEach(player => player?.dispose());
    };
  }, [samplesLoaded, loadingSamples]);

  // Update input gain when slider changes during recording
  useEffect(() => {
    if (gainNodesRef.current['input']) {
      gainNodesRef.current['input'].gain.value = inputGain;
    }
  }, [inputGain]);


  // Convert recording to MIDI/Samples (Beat mode) or Notes (Melody mode)
  const convertToMidi = async (trackId) => {
    const track = tracks.find(t => t.id === trackId);
    if (!track || !track.audioBlob) {
      toast.error('No recording to convert');
      return;
    }
    
    const trackMode = track.mode || 'beat';
    setIsConverting(true);
    
    try {
      if (trackMode === 'melody') {
        // Use Basic Pitch for melody transcription
        const formData = new FormData();
        formData.append('audio', track.audioBlob, 'recording.webm');
        formData.append('bpm', bpm.toString());
        formData.append('quantize', quantizeEnabled.toString());
        formData.append('grid_division', gridSize.toString());
        
        const response = await axios.post(`${API}/api/ai-audio/detect-melody`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        if (response.data.success) {
          // Update track with melody MIDI events
          setTracks(prevTracks => prevTracks.map(t => 
            t.id === trackId 
              ? { 
                  ...t, 
                  midiEvents: response.data.midi_events,
                  pattern: [], // Melody doesn't use step pattern
                  isConverted: true,
                  duration: Math.max(t.duration, response.data.duration)
                }
              : t
          ));
          
          toast.success(`🎵 Detected ${response.data.note_count} notes! Click Play to hear the melody.`);
        } else {
          throw new Error('Melody detection failed');
        }
      } else {
        // Use existing beat detection for drums
        const arrayBuffer = await track.audioBlob.arrayBuffer();
        const base64 = btoa(String.fromCharCode(...new Uint8Array(arrayBuffer)));
        const audioData = `data:audio/webm;base64,${base64}`;
        
        const response = await axios.post(`${API}/api/ai-audio/detect-beats`, {
          audio_data: audioData,
          bpm: bpm,
          quantize: quantizeEnabled,
          grid_division: gridSize
        });
        
        if (response.data.success) {
          // Update track with MIDI events and pattern
          setTracks(prevTracks => prevTracks.map(t => 
            t.id === trackId 
              ? { 
                  ...t, 
                  midiEvents: response.data.midi_events,
                  pattern: response.data.pattern,
                  isConverted: true,
                  duration: Math.max(t.duration, response.data.duration)
                }
              : t
          ));
          
          toast.success(`🥁 Detected ${response.data.total_beats} beats! Click Play to hear with real drums.`);
        } else {
          throw new Error('Beat detection failed');
        }
      }
    } catch (err) {
      console.error('Conversion error:', err);
      const errorMsg = trackMode === 'melody' 
        ? 'Failed to detect melody. Try humming clearer notes.'
        : 'Failed to detect beats. Try recording with clearer beats.';
      toast.error(errorMsg);
    } finally {
      setIsConverting(false);
    }
  };

  // Play track with samples (MIDI events)
  const playTrackWithSamples = async (track) => {
    if (!track.midiEvents || track.midiEvents.length === 0) return;
    if (!samplesLoaded) {
      toast.error('Samples not loaded yet');
      return;
    }
    
    await Tone.start();
    Tone.Transport.bpm.value = bpm;
    
    // Create a sequence from MIDI events
    const events = track.midiEvents.map(event => ({
      time: event.time,
      instrument: event.instrument,
      velocity: event.velocity
    }));
    
    // Schedule each event
    events.forEach(event => {
      Tone.Transport.schedule((time) => {
        const player = samplePlayersRef.current[event.instrument];
        if (player && player.loaded) {
          player.volume.value = (event.velocity / 127) * 6 - 6; // Convert velocity to dB
          player.start(time);
        }
      }, event.time);
    });
    
    Tone.Transport.start();
  };

  // Request microphone permission on page load
  useEffect(() => {
    const requestMicPermission = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true }
        });
        // Store the stream for later use
        micStreamRef.current = stream;
        setMicPermission('granted');
        toast.success('Microphone ready!', { duration: 2000 });
      } catch (err) {
        console.error('Mic permission error:', err);
        if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
          setMicPermission('denied');
          toast.error('Microphone access denied. Please allow microphone access to record.');
        } else {
          setMicPermission('denied');
          toast.error('Could not access microphone.');
        }
      }
    };
    
    requestMicPermission();
    
    return () => {
      // Don't stop the stream on unmount - we want to keep it active
    };
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't trigger shortcuts when typing in inputs
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') {
        return;
      }
      
      switch (e.code) {
        case 'Space':
          e.preventDefault();
          if (isRecording) {
            // Stop recording if recording
            stopRecording();
          } else if (tracks.some(t => t.audioBlob)) {
            // Play/pause if there are tracks
            playAllTracks();
          }
          break;
        case 'KeyR':
          e.preventDefault();
          if (!isRecording && !isCountingIn && selectedTrackId && micPermission === 'granted') {
            startWithCountIn();
          }
          break;
        case 'KeyS':
          e.preventDefault();
          if (isRecording) stopRecording();
          if (isPlaying) stopPlayback();
          break;
        case 'Home':
        case 'Backspace':
          if (e.code === 'Backspace') e.preventDefault();
          rewind();
          break;
        case 'End':
          // Fast forward to end
          const maxDur = Math.max(...tracks.map(t => t.duration || 0));
          setPlaybackPosition(maxDur);
          break;
        case 'ArrowLeft':
          e.preventDefault();
          setPlaybackPosition(prev => Math.max(0, prev - 1));
          break;
        case 'ArrowRight':
          e.preventDefault();
          const maxDuration = Math.max(...tracks.map(t => t.duration || 0));
          setPlaybackPosition(prev => Math.min(maxDuration, prev + 1));
          break;
        default:
          break;
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isRecording, isPlaying, isCountingIn, selectedTrackId, micPermission, tracks]);

  // Initialize on mount
  useEffect(() => {
    // Set first track as selected
    if (tracks.length > 0 && !selectedTrackId) {
      setSelectedTrackId(tracks[0].id);
    }
    
    // Initialize metronome
    metronomeRef.current = new Tone.MembraneSynth({
      pitchDecay: 0.01,
      octaves: 2,
      envelope: { attack: 0.001, decay: 0.1, sustain: 0, release: 0.1 }
    }).toDestination();
    metronomeRef.current.volume.value = -10;
    
    return () => {
      metronomeRef.current?.dispose();
      Object.values(audioPlayersRef.current).forEach(player => {
        if (player) {
          player.pause();
          player.src = '';
        }
      });
      // Stop mic stream on unmount
      if (micStreamRef.current) {
        micStreamRef.current.getTracks().forEach(t => t.stop());
      }
    };
  }, []);

  // Draw waveform for a track
  const drawWaveform = useCallback((trackId, audioBuffer) => {
    const canvas = waveformCanvasRefs.current[trackId];
    if (!canvas || !audioBuffer) return;
    
    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;
    const data = audioBuffer.getChannelData(0);
    
    // Find track color
    const trackIndex = tracks.findIndex(t => t.id === trackId);
    const color = TRACK_COLORS[trackIndex % TRACK_COLORS.length];
    
    // Clear canvas
    ctx.fillStyle = '#1c1917';
    ctx.fillRect(0, 0, width, height);
    
    // Draw waveform
    const step = Math.ceil(data.length / width);
    const amp = height / 2;
    
    ctx.beginPath();
    ctx.moveTo(0, amp);
    
    for (let i = 0; i < width; i++) {
      let min = 1.0;
      let max = -1.0;
      
      for (let j = 0; j < step; j++) {
        const datum = data[(i * step) + j];
        if (datum < min) min = datum;
        if (datum > max) max = datum;
      }
      
      ctx.lineTo(i, (1 + min) * amp);
      ctx.lineTo(i, (1 + max) * amp);
    }
    
    ctx.strokeStyle = color.wave;
    ctx.lineWidth = 1;
    ctx.stroke();
    
    // Add glow effect
    ctx.shadowColor = color.wave;
    ctx.shadowBlur = 5;
    ctx.stroke();
    ctx.shadowBlur = 0;
    
    // Draw center line
    ctx.beginPath();
    ctx.moveTo(0, amp);
    ctx.lineTo(width, amp);
    ctx.strokeStyle = 'rgba(255,255,255,0.1)';
    ctx.stroke();
  }, [tracks]);

  // Draw input level meter
  const drawLevelMeter = useCallback(() => {
    if (!levelCanvasRef.current || !analyserRef.current) return;
    
    const canvas = levelCanvasRef.current;
    const ctx = canvas.getContext('2d');
    const analyser = analyserRef.current;
    const dataArray = new Uint8Array(analyser.frequencyBinCount);
    
    const drawLevel = () => {
      levelAnimationRef.current = requestAnimationFrame(drawLevel);
      analyser.getByteTimeDomainData(dataArray);
      
      let sum = 0;
      for (let i = 0; i < dataArray.length; i++) {
        const val = (dataArray[i] - 128) / 128;
        sum += val * val;
      }
      const level = Math.min(1, Math.sqrt(sum / dataArray.length) * 3);
      
      setInputLevel(level);
      if (level > peakLevel) setPeakLevel(level);
      
      // Draw meter
      ctx.fillStyle = '#1c1917';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      const barCount = 20;
      const barWidth = (canvas.width - (barCount - 1) * 2) / barCount;
      const activeBars = Math.floor(level * barCount);
      
      for (let i = 0; i < barCount; i++) {
        const color = i < barCount * 0.6 ? '#22c55e' : i < barCount * 0.8 ? '#eab308' : '#ef4444';
        ctx.fillStyle = i < activeBars ? color : '#374151';
        ctx.fillRect(i * (barWidth + 2), 4, barWidth, canvas.height - 8);
      }
    };
    drawLevel();
  }, [peakLevel]);

  // Reset peak level periodically
  useEffect(() => {
    const interval = setInterval(() => setPeakLevel(p => Math.max(0, p - 0.02)), 100);
    return () => clearInterval(interval);
  }, []);

  // Metronome functions
  const startMetronome = useCallback(async () => {
    await Tone.start();
    const interval = (60 / bpm) * 1000;
    let beat = 0;
    const tick = () => {
      metronomeRef.current?.triggerAttackRelease(beat % 4 === 0 ? 'C3' : 'G3', '16n');
      setMetronomeCount(beat % 4);
      beat++;
    };
    tick();
    metronomeIntervalRef.current = setInterval(tick, interval);
  }, [bpm]);

  const stopMetronome = useCallback(() => {
    if (metronomeIntervalRef.current) {
      clearInterval(metronomeIntervalRef.current);
      metronomeIntervalRef.current = null;
    }
    setMetronomeCount(0);
  }, []);

  // Add new track - FIX: Use functional update and immediate selection
  const addTrack = useCallback(() => {
    if (tracks.length >= 6) {
      toast.error('Maximum 6 tracks allowed');
      return;
    }
    const newTrackId = generateId();
    const newTrackNum = tracks.length + 1;
    const newTrack = {
      id: newTrackId,
      name: `Track ${newTrackNum}`,
      audioBlob: null,
      waveformData: null,
      midiEvents: [],
      pattern: [],
      volume: 0.8,
      muted: false,
      solo: false,
      duration: 0,
      isConverted: false,
      mode: recordingMode,  // Track inherits current recording mode
      currentLevel: 0
    };
    
    // Set both states atomically - selectedTrackId FIRST so recording targets it
    setSelectedTrackId(newTrackId);
    setTracks(prevTracks => [...prevTracks, newTrack]);
    
    toast.success(`Added Track ${newTrackNum} (${recordingMode === 'beat' ? 'Beat' : 'Melody'}) - now selected for recording`);
  }, [tracks.length, recordingMode]);

  // Delete track
  const deleteTrack = (trackId) => {
    if (tracks.length <= 1) {
      toast.error('Need at least one track');
      return;
    }
    
    // Clean up audio player
    if (audioPlayersRef.current[trackId]) {
      audioPlayersRef.current[trackId].pause();
      audioPlayersRef.current[trackId].src = '';
      delete audioPlayersRef.current[trackId];
    }
    
    const newTracks = tracks.filter(t => t.id !== trackId);
    setTracks(newTracks);
    
    if (selectedTrackId === trackId) {
      setSelectedTrackId(newTracks[0]?.id || null);
    }
    toast.success('Track deleted');
  };

  // Update track property
  const updateTrack = (trackId, updates) => {
    setTracks(tracks.map(t => t.id === trackId ? { ...t, ...updates } : t));
  };

  // Count-in then record
  const startWithCountIn = useCallback(async () => {
    if (!selectedTrackId) {
      toast.error('Select a track first');
      return;
    }
    
    if (!metronomeEnabled) {
      startRecording();
      return;
    }
    
    setIsCountingIn(true);
    setActiveTrackId(selectedTrackId);
    await Tone.start();
    
    const interval = (60 / bpm) * 1000;
    let count = 0;
    
    const countInInterval = setInterval(() => {
      metronomeRef.current?.triggerAttackRelease(count === 0 ? 'C4' : 'G3', '16n');
      setMetronomeCount(count);
      count++;
      if (count >= 4) {
        clearInterval(countInInterval);
        setIsCountingIn(false);
        startRecording();
        if (metronomeEnabled) startMetronome();
      }
    }, interval);
  }, [bpm, metronomeEnabled, selectedTrackId, startMetronome]);

  // Start recording
  const startRecording = async () => {
    if (!selectedTrackId) {
      toast.error('Select a track first');
      return;
    }
    
    // Store the track ID we're recording to in a ref (survives state changes)
    recordingTrackIdRef.current = selectedTrackId;
    
    try {
      // Always try to get a fresh stream for recording to avoid stale stream issues
      let stream;
      try {
        // First try to use existing stream if it's still active
        if (micStreamRef.current && micStreamRef.current.getTracks().every(t => t.readyState === 'live')) {
          stream = micStreamRef.current;
        } else {
          // Request new stream
          stream = await navigator.mediaDevices.getUserMedia({
            audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true }
          });
          micStreamRef.current = stream;
          setMicPermission('granted');
        }
      } catch (micError) {
        console.error('Mic error:', micError);
        setMicPermission('denied');
        toast.error('Could not access microphone. Please allow microphone access in your browser settings.');
        return;
      }
      
      streamRef.current = stream;
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      const source = audioContextRef.current.createMediaStreamSource(stream);
      
      // Create gain node for input level control
      const gainNode = audioContextRef.current.createGain();
      gainNode.gain.value = inputGain;
      gainNodesRef.current['input'] = gainNode;
      
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 2048;
      
      // Connect: source -> gain -> analyser (and to destination for monitoring if needed)
      source.connect(gainNode);
      gainNode.connect(analyserRef.current);
      
      // Create a MediaStreamDestination for the gain-adjusted audio
      const gainedDestination = audioContextRef.current.createMediaStreamDestination();
      gainNode.connect(gainedDestination);
      
      drawLevelMeter();
      
      const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 
                       MediaRecorder.isTypeSupported('audio/mp4') ? 'audio/mp4' : 'audio/wav';
      
      // Record the gain-adjusted stream instead of raw mic stream
      mediaRecorderRef.current = new MediaRecorder(gainedDestination.stream, { mimeType });
      chunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      
      mediaRecorderRef.current.onstop = async () => {
        // Use the ref to get the correct track ID
        const targetTrackId = recordingTrackIdRef.current;
        
        if (!targetTrackId) {
          console.error('No target track ID for recording');
          return;
        }
        
        const blob = new Blob(chunksRef.current, { type: mimeType });
        
        // Decode audio to get waveform data
        try {
          const arrayBuffer = await blob.arrayBuffer();
          const tempContext = new (window.AudioContext || window.webkitAudioContext)();
          const audioBuffer = await tempContext.decodeAudioData(arrayBuffer);
          
          // Update track with recording using functional update to ensure we get latest state
          // Clear previous MIDI data when re-recording
          setTracks(prevTracks => prevTracks.map(t => 
            t.id === targetTrackId 
              ? { 
                  ...t, 
                  audioBlob: blob, 
                  waveformData: audioBuffer, 
                  duration: audioBuffer.duration,
                  midiEvents: [],      // Clear MIDI events
                  pattern: [],         // Clear pattern
                  isConverted: false   // Reset converted flag
                }
              : t
          ));
          
          // Draw waveform after state update
          setTimeout(() => {
            drawWaveform(targetTrackId, audioBuffer);
          }, 200);
          
          toast.success('Recording saved to track!');
        } catch (decodeError) {
          console.error('Failed to decode audio:', decodeError);
          toast.error('Failed to process recording');
        }
      };
      
      mediaRecorderRef.current.start(100);
      setIsRecording(true);
      setRecordingTime(0);
      setActiveTrackId(selectedTrackId);
      
      toast.success('Recording started - beatbox or hum your part!');
      
    } catch (err) {
      console.error('Recording error:', err);
      toast.error('Could not access microphone');
    }
  };

  // Stop recording
  const stopRecording = () => {
    mediaRecorderRef.current?.state !== 'inactive' && mediaRecorderRef.current?.stop();
    streamRef.current?.getTracks().forEach(t => t.stop());
    levelAnimationRef.current && cancelAnimationFrame(levelAnimationRef.current);
    stopMetronome();
    setIsRecording(false);
    setIsCountingIn(false);
    setActiveTrackId(null);
  };

  // Recording timer
  useEffect(() => {
    let interval;
    if (isRecording) {
      interval = setInterval(() => {
        setRecordingTime(prev => {
          if (prev >= 30) { // Max 30 seconds per track
            stopRecording();
            return prev;
          }
          return prev + 0.1;
        });
      }, 100);
    }
    return () => clearInterval(interval);
  }, [isRecording]);

  // Play all tracks
  const playAllTracks = async () => {
    if (isPlaying) {
      // Stop playback
      Tone.Transport.stop();
      Tone.Transport.cancel();
      Object.values(audioPlayersRef.current).forEach(player => {
        if (player) player.pause();
      });
      if (playbackIntervalRef.current) {
        clearInterval(playbackIntervalRef.current);
      }
      setIsPlaying(false);
      return;
    }
    
    // Check if any tracks have audio or MIDI
    const tracksWithContent = tracks.filter(t => (t.audioBlob || t.midiEvents?.length > 0) && !t.muted);
    if (tracksWithContent.length === 0) {
      toast.error('No tracks to play - record something first!');
      return;
    }
    
    // Check for solo tracks
    const soloTracks = tracks.filter(t => t.solo && (t.audioBlob || t.midiEvents?.length > 0));
    const tracksToPlay = soloTracks.length > 0 ? soloTracks : tracksWithContent;
    
    await Tone.start();
    Tone.Transport.bpm.value = bpm;
    Tone.Transport.cancel(); // Clear any previous scheduled events
    Tone.Transport.position = 0;
    
    // Play each track
    for (const track of tracksToPlay) {
      if (track.isConverted && track.midiEvents?.length > 0) {
        // Check if this is a melody or beat track
        if (track.mode === 'melody') {
          // Create synth for melody playback if not exists
          if (!melodySynthRef.current) {
            melodySynthRef.current = new Tone.PolySynth(Tone.Synth, {
              oscillator: { type: 'triangle' },
              envelope: { attack: 0.02, decay: 0.1, sustain: 0.3, release: 0.8 }
            }).toDestination();
          }
          
          // Schedule melody notes
          const part = new Tone.Part((time, event) => {
            if (!track.muted && melodySynthRef.current) {
              const vol = ((event.velocity / 127) * 12 - 12) + (track.volume * 6 - 6);
              melodySynthRef.current.volume.value = vol;
              melodySynthRef.current.triggerAttackRelease(event.note, event.duration, time);
            }
          }, track.midiEvents.map(e => ({ time: e.time, note: e.note, duration: e.duration || 0.25, velocity: e.velocity })));
          
          part.start(0);
          if (!sequenceRef.current) sequenceRef.current = [];
          sequenceRef.current.push(part);
          
        } else if (samplesLoaded) {
          // Play with drum samples (MIDI events) using Tone.js Part for precise timing
          const part = new Tone.Part((time, event) => {
            const player = samplePlayersRef.current[event.instrument];
            if (player && player.loaded && !track.muted) {
              // Set volume based on velocity and track volume
              const vol = ((event.velocity / 127) * 12 - 12) + (track.volume * 6 - 6);
              player.volume.value = vol;
              player.start(time);
            }
          }, track.midiEvents.map(e => ({ time: e.time, ...e })));
          
          part.start(0);
          
          // Store part for cleanup
          if (!sequenceRef.current) sequenceRef.current = [];
          sequenceRef.current.push(part);
        }
      } else if (track.audioBlob) {
        // Play original audio using Tone.js Player for proper metering
        try {
          const blobUrl = URL.createObjectURL(track.audioBlob);
          
          // Create Tone.Meter for this track
          const meter = new Tone.Meter({ smoothing: 0.8 });
          toneMetersRef.current[track.id] = meter;
          
          // Create Tone.Player and connect through meter
          const player = new Tone.Player({
            url: blobUrl,
            onload: () => {
              player.volume.value = Tone.gainToDb(track.volume);
              player.start(undefined, playbackPosition);
            }
          }).connect(meter).toDestination();
          
          tonePlayersRef.current[track.id] = player;
          
          // Also keep HTML5 Audio as fallback for position tracking
          const audio = new Audio(blobUrl);
          audio.volume = track.volume;
          audioPlayersRef.current[track.id] = audio;
        } catch (err) {
          console.error('Error setting up audio player with meter:', err);
          // Fallback to simple HTML5 Audio
          const audio = new Audio(URL.createObjectURL(track.audioBlob));
          audio.volume = track.volume;
          audioPlayersRef.current[track.id] = audio;
          audio.currentTime = playbackPosition;
          audio.play();
        }
      }
    }
    
    // Start Tone.js transport for MIDI playback
    Tone.Transport.start();
    setIsPlaying(true);
    
    // Update playback position
    const maxDuration = Math.max(...tracksToPlay.map(t => t.duration || 0), 2);
    playbackIntervalRef.current = setInterval(() => {
      setPlaybackPosition(prev => {
        const newPos = prev + 0.1;
        if (newPos >= maxDuration) {
          // Stop at end
          stopPlayback();
          return 0;
        }
        return newPos;
      });
    }, 100);
  };

  // Stop playback
  const stopPlayback = () => {
    Tone.Transport.stop();
    Tone.Transport.cancel();
    
    // Clean up Tone.js Parts
    if (sequenceRef.current) {
      sequenceRef.current.forEach(part => part?.dispose());
      sequenceRef.current = [];
    }
    
    // Clean up Tone.js Players and Meters
    Object.values(tonePlayersRef.current).forEach(player => {
      try {
        if (player) {
          player.stop();
          player.dispose();
        }
      } catch (e) {}
    });
    tonePlayersRef.current = {};
    
    Object.values(toneMetersRef.current).forEach(meter => {
      try {
        if (meter) meter.dispose();
      } catch (e) {}
    });
    toneMetersRef.current = {};
    
    // Stop HTML5 audio elements
    Object.values(audioPlayersRef.current).forEach(player => {
      if (player) {
        player.pause();
        player.currentTime = 0;
      }
    });
    
    if (playbackIntervalRef.current) {
      clearInterval(playbackIntervalRef.current);
    }
    setIsPlaying(false);
    setPlaybackPosition(0);
    // Reset track levels
    setTrackLevels({});
  };

  // Update track level meters during playback - using real audio analysis
  useEffect(() => {
    if (!isPlaying) {
      setTrackLevels({});
      return;
    }
    
    const levelInterval = setInterval(() => {
      const newLevels = {};
      tracks.forEach(track => {
        if (track.muted) {
          newLevels[track.id] = 0;
        } else {
          // Try to get real level from Tone.Meter
          const meter = toneMetersRef.current[track.id];
          if (meter) {
            try {
              const dbLevel = meter.getValue();
              // Convert dB to 0-1 range (-60dB to 0dB)
              const normalizedLevel = Math.max(0, Math.min(1, (dbLevel + 60) / 60));
              newLevels[track.id] = normalizedLevel * track.volume;
            } catch (e) {
              newLevels[track.id] = 0;
            }
          } else if (audioAnalysersRef.current[track.id]) {
            // Fallback to Web Audio analyser for HTML5 Audio elements
            const analyser = audioAnalysersRef.current[track.id];
            const dataArray = new Uint8Array(analyser.frequencyBinCount);
            analyser.getByteFrequencyData(dataArray);
            
            // Calculate RMS level
            let sum = 0;
            for (let i = 0; i < dataArray.length; i++) {
              sum += dataArray[i] * dataArray[i];
            }
            const rms = Math.sqrt(sum / dataArray.length);
            const normalizedLevel = rms / 255;
            newLevels[track.id] = normalizedLevel * track.volume;
          } else if (track.audioBlob || (track.isConverted && track.midiEvents?.length > 0)) {
            // Fallback: estimate level based on volume (smooth animation)
            const baseLevel = track.volume * 0.6;
            const time = Date.now() / 1000;
            const variation = Math.sin(time * 10 + track.id.charCodeAt(0)) * 0.15 + 0.15;
            newLevels[track.id] = Math.min(1, baseLevel + variation);
          } else {
            newLevels[track.id] = 0;
          }
        }
      });
      setTrackLevels(newLevels);
    }, 50); // Update at ~20fps
    
    return () => clearInterval(levelInterval);
  }, [isPlaying, tracks]);

  // Rewind (just reset position, don't delete!)
  const rewind = () => {
    setPlaybackPosition(0);
    Object.values(audioPlayersRef.current).forEach(player => {
      if (player) player.currentTime = 0;
    });
    toast.success('Rewound to start');
  };

  // Get max duration across all tracks
  const maxTrackDuration = Math.max(...tracks.map(t => t.duration || 0), 0);

  // Format time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    const ms = Math.floor((seconds % 1) * 10);
    return `${mins}:${secs.toString().padStart(2, '0')}.${ms}`;
  };

  // Validate Replicate key
  const validateReplicateKey = async (key) => {
    if (!key || !key.startsWith('r8_')) {
      setIsKeyValid(false);
      return false;
    }
    setIsValidatingKey(true);
    try {
      const formData = new FormData();
      formData.append('api_key', key);
      const res = await axios.post(`${API}/api/ai-audio/validate-key`, formData);
      setIsKeyValid(res.data.valid);
      if (res.data.valid) {
        localStorage.setItem('replicate_api_key', key);
        toast.success('API key saved!');
      } else {
        toast.error(res.data.message || 'Invalid API key');
      }
      return res.data.valid;
    } catch {
      setIsKeyValid(false);
      toast.error('Failed to validate key');
      return false;
    } finally {
      setIsValidatingKey(false);
    }
  };

  // Community beats
  const loadCommunityBeats = async () => {
    setLoadingBeats(true);
    try {
      setCommunityBeats((await axios.get(`${API}/api/ai-audio/beats?limit=10`)).data.beats);
    } catch {
      toast.error('Failed to load');
    } finally {
      setLoadingBeats(false);
    }
  };

  const copyShareUrl = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    toast.success('Link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  // Export functions
  const exportToMIDI = () => {
    // Get all tracks with MIDI events
    const midiTracks = tracks.filter(t => t.midiEvents && t.midiEvents.length > 0);
    
    if (midiTracks.length === 0) {
      toast.error('No MIDI data to export. Record and convert tracks first.');
      return;
    }
    
    // Create MIDI file using simple format
    // MIDI Header: "MThd" + length (6) + format (0) + numTracks (1) + division (480)
    const ticksPerBeat = 480;
    const microsecondsPerBeat = Math.round(60000000 / bpm);
    
    // Collect all events from all tracks
    let allEvents = [];
    midiTracks.forEach(track => {
      track.midiEvents.forEach(event => {
        const tickTime = Math.round(event.time * ticksPerBeat * (bpm / 60));
        allEvents.push({
          tick: tickTime,
          type: 'noteOn',
          note: event.note,
          velocity: event.velocity || 100
        });
        const durationTicks = Math.round(event.duration * ticksPerBeat * (bpm / 60));
        allEvents.push({
          tick: tickTime + durationTicks,
          type: 'noteOff',
          note: event.note,
          velocity: 0
        });
      });
    });
    
    // Sort by tick time
    allEvents.sort((a, b) => a.tick - b.tick);
    
    // Build track data
    const trackData = [];
    
    // Tempo event
    trackData.push(0x00, 0xFF, 0x51, 0x03);
    trackData.push((microsecondsPerBeat >> 16) & 0xFF);
    trackData.push((microsecondsPerBeat >> 8) & 0xFF);
    trackData.push(microsecondsPerBeat & 0xFF);
    
    // Note events with delta times
    let lastTick = 0;
    allEvents.forEach(event => {
      const delta = event.tick - lastTick;
      lastTick = event.tick;
      
      // Variable length delta time
      if (delta < 128) {
        trackData.push(delta);
      } else {
        const bytes = [];
        let d = delta;
        bytes.push(d & 0x7F);
        d >>= 7;
        while (d > 0) {
          bytes.push((d & 0x7F) | 0x80);
          d >>= 7;
        }
        bytes.reverse().forEach(b => trackData.push(b));
      }
      
      // Note on/off
      if (event.type === 'noteOn') {
        trackData.push(0x90, event.note, event.velocity);
      } else {
        trackData.push(0x80, event.note, 0);
      }
    });
    
    // End of track
    trackData.push(0x00, 0xFF, 0x2F, 0x00);
    
    // Build complete MIDI file
    const midiData = [];
    
    // Header chunk
    midiData.push(0x4D, 0x54, 0x68, 0x64); // "MThd"
    midiData.push(0x00, 0x00, 0x00, 0x06); // Length = 6
    midiData.push(0x00, 0x00); // Format = 0
    midiData.push(0x00, 0x01); // Num tracks = 1
    midiData.push((ticksPerBeat >> 8) & 0xFF, ticksPerBeat & 0xFF); // Division
    
    // Track chunk
    midiData.push(0x4D, 0x54, 0x72, 0x6B); // "MTrk"
    const trackLen = trackData.length;
    midiData.push((trackLen >> 24) & 0xFF, (trackLen >> 16) & 0xFF, (trackLen >> 8) & 0xFF, trackLen & 0xFF);
    trackData.forEach(b => midiData.push(b));
    
    // Download
    const blob = new Blob([new Uint8Array(midiData)], { type: 'audio/midi' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `batucada_${Date.now()}.mid`;
    a.click();
    URL.revokeObjectURL(url);
    
    toast.success('MIDI file exported!');
  };

  const exportToWAV = async () => {
    const tracksWithAudio = tracks.filter(t => t.audioBlob);
    
    if (tracksWithAudio.length === 0) {
      toast.error('No audio to export. Record some tracks first.');
      return;
    }
    
    toast.info('Preparing WAV export...');
    
    try {
      // For single track, just export directly
      if (tracksWithAudio.length === 1 && tracksWithAudio[0].audioBlob) {
        const blob = tracksWithAudio[0].audioBlob;
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `batucada_${Date.now()}.wav`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success('WAV file exported!');
        return;
      }
      
      // For multiple tracks, use OfflineAudioContext to mix
      const sampleRate = 44100;
      const maxDuration = Math.max(...tracksWithAudio.map(t => t.duration || 5)) + 1;
      const offlineCtx = new OfflineAudioContext(2, sampleRate * maxDuration, sampleRate);
      
      // Decode and schedule all tracks
      for (const track of tracksWithAudio) {
        if (track.audioBlob) {
          const arrayBuffer = await track.audioBlob.arrayBuffer();
          const audioBuffer = await offlineCtx.decodeAudioData(arrayBuffer);
          
          const source = offlineCtx.createBufferSource();
          source.buffer = audioBuffer;
          
          const gainNode = offlineCtx.createGain();
          gainNode.gain.value = track.muted ? 0 : track.volume;
          
          source.connect(gainNode);
          gainNode.connect(offlineCtx.destination);
          source.start(0);
        }
      }
      
      // Render
      const renderedBuffer = await offlineCtx.startRendering();
      
      // Convert to WAV
      const wavData = audioBufferToWav(renderedBuffer);
      const blob = new Blob([wavData], { type: 'audio/wav' });
      
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `batucada_mix_${Date.now()}.wav`;
      a.click();
      URL.revokeObjectURL(url);
      
      toast.success('WAV mix exported!');
    } catch (err) {
      console.error('Export failed:', err);
      toast.error('Export failed: ' + err.message);
    }
  };

  // Helper: Convert AudioBuffer to WAV format
  const audioBufferToWav = (buffer) => {
    const numChannels = buffer.numberOfChannels;
    const sampleRate = buffer.sampleRate;
    const format = 1; // PCM
    const bitDepth = 16;
    
    const bytesPerSample = bitDepth / 8;
    const blockAlign = numChannels * bytesPerSample;
    
    const samples = buffer.length;
    const dataSize = samples * blockAlign;
    const bufferSize = 44 + dataSize;
    
    const arrayBuffer = new ArrayBuffer(bufferSize);
    const view = new DataView(arrayBuffer);
    
    // WAV header
    const writeString = (offset, string) => {
      for (let i = 0; i < string.length; i++) {
        view.setUint8(offset + i, string.charCodeAt(i));
      }
    };
    
    writeString(0, 'RIFF');
    view.setUint32(4, bufferSize - 8, true);
    writeString(8, 'WAVE');
    writeString(12, 'fmt ');
    view.setUint32(16, 16, true); // fmt chunk size
    view.setUint16(20, format, true);
    view.setUint16(22, numChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * blockAlign, true);
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, bitDepth, true);
    writeString(36, 'data');
    view.setUint32(40, dataSize, true);
    
    // Interleave channels and write samples
    const channels = [];
    for (let i = 0; i < numChannels; i++) {
      channels.push(buffer.getChannelData(i));
    }
    
    let offset = 44;
    for (let i = 0; i < samples; i++) {
      for (let ch = 0; ch < numChannels; ch++) {
        const sample = Math.max(-1, Math.min(1, channels[ch][i]));
        const intSample = sample < 0 ? sample * 0x8000 : sample * 0x7FFF;
        view.setInt16(offset, intSample, true);
        offset += 2;
      }
    }
    
    return arrayBuffer;
  };

  const exportToMP3 = async () => {
    // MP3 encoding requires a library like lamejs
    // For now, export as WAV and inform user
    toast.info('MP3 export coming soon! Exporting as WAV instead...');
    await exportToWAV();
  };

  // Check key on mount
  useEffect(() => {
    if (replicateKey && replicateKey.startsWith('r8_')) {
      setIsKeyValid(true);
    }
  }, [replicateKey]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-950 via-stone-900 to-stone-950 text-white">
      {/* Header */}
      <div className="bg-stone-900/80 backdrop-blur-lg border-b border-stone-800 px-4 py-3 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/brazilian-grooves" className="p-2 hover:bg-stone-800 rounded-lg transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-xl font-bold bg-gradient-to-r from-amber-400 to-orange-500 bg-clip-text text-transparent">
                Play What You Say
              </h1>
              <p className="text-xs text-stone-400">Multi-track voice recorder • Layer beats and melodies</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Link 
              to="/you-say-we-play" 
              className="px-3 py-1.5 bg-gradient-to-r from-pink-500 to-purple-500 rounded-lg text-sm font-medium hover:opacity-90 flex items-center gap-1.5"
              data-testid="ai-features-link"
            >
              <Sparkles className="w-4 h-4" />
              AI Features
            </Link>
            
            {/* Export Dropdown */}
            <div className="relative group">
              <button className="px-3 py-1.5 bg-green-600 hover:bg-green-500 rounded-lg text-sm font-medium flex items-center gap-1.5" data-testid="export-btn">
                <Download className="w-4 h-4" />
                Export
              </button>
              <div className="absolute right-0 top-full mt-1 bg-stone-800 border border-stone-700 rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 min-w-[140px]">
                <button onClick={exportToMIDI} className="w-full px-4 py-2 text-left text-sm hover:bg-stone-700 flex items-center gap-2 rounded-t-lg">
                  <FileMusic className="w-4 h-4 text-blue-400" />
                  Export MIDI
                </button>
                <button onClick={exportToWAV} className="w-full px-4 py-2 text-left text-sm hover:bg-stone-700 flex items-center gap-2">
                  <FileAudio className="w-4 h-4 text-amber-400" />
                  Export WAV
                </button>
                <button onClick={exportToMP3} className="w-full px-4 py-2 text-left text-sm hover:bg-stone-700 flex items-center gap-2 rounded-b-lg text-stone-400">
                  <FileAudio className="w-4 h-4" />
                  Export MP3
                </button>
              </div>
            </div>
            
            <button onClick={() => setShowSettings(true)} className="p-2 hover:bg-stone-800 rounded-lg" title="Settings">
              <Settings className="w-5 h-5 text-stone-400" />
            </button>
            <button onClick={() => { setShowCommunityBeats(true); loadCommunityBeats(); }} 
              className="p-2 hover:bg-stone-800 rounded-lg" title="Community">
              <Users className="w-5 h-5 text-stone-400" />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-4">
        {/* Main DAW Layout */}
        <div className="grid grid-cols-12 gap-4">
          
          {/* Track List & Mixer - Left Side */}
          <div className="col-span-3 space-y-3">
            {/* Add Track Button - More prominent */}
            <button
              onClick={addTrack}
              disabled={tracks.length >= 6}
              className="w-full py-3 bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500 text-black font-bold rounded-lg text-sm flex items-center justify-center gap-2 disabled:opacity-50 disabled:from-stone-700 disabled:to-stone-700 disabled:text-stone-400"
            >
              <Plus className="w-5 h-5" /> Add New Track ({tracks.length}/6)
            </button>
            
            {/* Track Mixer */}
            <div className="bg-stone-900 rounded-xl border border-stone-800 p-3 space-y-2">
              <h3 className="text-xs font-bold text-stone-400 uppercase tracking-wider mb-3">Mixer</h3>
              
              {tracks.map((track, index) => {
                const color = TRACK_COLORS[index % TRACK_COLORS.length];
                const isSelected = selectedTrackId === track.id;
                const isActive = activeTrackId === track.id;
                
                // Clear track function
                const clearTrack = (e) => {
                  e.stopPropagation();
                  setTracks(prevTracks => prevTracks.map(t => 
                    t.id === track.id 
                      ? { ...t, audioBlob: null, waveformData: null, midiEvents: [], pattern: [], duration: 0, isConverted: false }
                      : t
                  ));
                  toast.success(`${track.name} cleared`);
                };
                
                return (
                  <div 
                    key={track.id}
                    onClick={() => setSelectedTrackId(track.id)}
                    className={`p-2 rounded-lg cursor-pointer transition-all ${color.bg} ${color.border} border
                      ${isSelected ? 'ring-2 ring-white/30' : ''}
                      ${isActive ? 'ring-2 ring-red-500 animate-pulse' : ''}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium truncate">{track.name}</span>
                      <div className="flex items-center gap-1">
                        {/* Clear button */}
                        {track.audioBlob && (
                          <button
                            onClick={clearTrack}
                            className="p-1 hover:bg-amber-500/20 rounded text-stone-400 hover:text-amber-400"
                            title="Clear track"
                          >
                            <RefreshCw className="w-3 h-3" />
                          </button>
                        )}
                        {/* Delete button */}
                        <button
                          onClick={(e) => { e.stopPropagation(); deleteTrack(track.id); }}
                          className="p-1 hover:bg-red-500/20 rounded text-stone-400 hover:text-red-400"
                          title="Delete track"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                    
                    {/* Volume Slider with Level Meter */}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => { e.stopPropagation(); updateTrack(track.id, { muted: !track.muted }); }}
                        className={`p-1 rounded ${track.muted ? 'bg-red-500/30 text-red-400' : 'text-stone-400'}`}
                      >
                        {track.muted ? <VolumeX className="w-3 h-3" /> : <Volume1 className="w-3 h-3" />}
                      </button>
                      
                      {/* Volume slider + level meter combo */}
                      <div className="flex-1 relative">
                        <input
                          type="range"
                          min="0"
                          max="1"
                          step="0.01"
                          value={track.volume}
                          onChange={(e) => {
                            e.stopPropagation();
                            updateTrack(track.id, { volume: parseFloat(e.target.value) });
                            if (audioPlayersRef.current[track.id]) {
                              audioPlayersRef.current[track.id].volume = parseFloat(e.target.value);
                            }
                          }}
                          className="w-full h-1 accent-amber-500 relative z-10"
                          onClick={(e) => e.stopPropagation()}
                        />
                        {/* Track Level Meter - shows during playback */}
                        <div className="absolute top-3 left-0 right-0 h-1.5 bg-stone-800 rounded overflow-hidden">
                          <div 
                            className={`h-full transition-all duration-75 ${
                              (trackLevels[track.id] || 0) > 0.8 ? 'bg-red-500' :
                              (trackLevels[track.id] || 0) > 0.5 ? 'bg-amber-500' : 'bg-green-500'
                            }`}
                            style={{ width: `${(trackLevels[track.id] || 0) * 100}%` }}
                          />
                        </div>
                      </div>
                      
                      <span className="text-xs text-stone-500 w-8">
                        {Math.round(track.volume * 100)}%
                      </span>
                    </div>
                    
                    {/* Track info with mode indicator */}
                    <div className="text-xs text-stone-500 mt-2 flex items-center justify-between">
                      <span>{track.duration > 0 ? formatTime(track.duration) : 'Empty'}</span>
                      {track.mode && (
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                          track.mode === 'beat' ? 'bg-amber-500/20 text-amber-400' : 'bg-purple-500/20 text-purple-400'
                        }`}>
                          {track.mode === 'beat' ? '🥁' : '🎵'}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Input Level Meter with Gain Control */}
            <div className="bg-stone-900 rounded-xl border border-stone-800 p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold text-stone-400 uppercase tracking-wider flex items-center gap-1">
                  <Volume2 className="w-3 h-3" /> Input
                </span>
                <div className="flex items-center gap-2">
                  {micPermission === 'granted' && (
                    <span className="text-xs text-green-400 flex items-center gap-1">
                      <Mic className="w-3 h-3" /> Ready
                    </span>
                  )}
                  {micPermission === 'denied' && (
                    <span className="text-xs text-red-400 flex items-center gap-1">
                      <Mic className="w-3 h-3" /> Denied
                    </span>
                  )}
                  {micPermission === 'pending' && (
                    <span className="text-xs text-amber-400 flex items-center gap-1">
                      <Loader2 className="w-3 h-3 animate-spin" /> Requesting...
                    </span>
                  )}
                  {isRecording && <span className="text-xs text-red-500 animate-pulse">REC</span>}
                </div>
              </div>
              {/* Level meter */}
              <canvas ref={levelCanvasRef} width={200} height={20} className="w-full h-5 rounded bg-stone-950" />
              {/* Input Gain Slider */}
              <div className="flex items-center gap-2 mt-2">
                <span className="text-[10px] text-stone-500">Gain</span>
                <input
                  type="range"
                  min="0.5"
                  max="2"
                  step="0.1"
                  value={inputGain}
                  onChange={(e) => setInputGain(parseFloat(e.target.value))}
                  className="flex-1 h-1 accent-green-500"
                />
                <span className="text-[10px] text-stone-500 w-8">
                  {Math.round(inputGain * 100)}%
                </span>
              </div>
            </div>
            
            {/* Recording Mode Toggle */}
            <div className="bg-stone-900 rounded-xl border border-stone-800 p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold text-stone-400 uppercase tracking-wider flex items-center gap-1">
                  <Music className="w-3 h-3" /> Mode
                </span>
              </div>
              <div className="flex gap-1">
                <button
                  onClick={() => setRecordingMode('beat')}
                  className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-1 ${
                    recordingMode === 'beat' 
                      ? 'bg-amber-500 text-black' 
                      : 'bg-stone-700 text-stone-400 hover:bg-stone-600'
                  }`}
                >
                  <Drum className="w-3 h-3" /> Beat
                </button>
                <button
                  onClick={() => setRecordingMode('melody')}
                  className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-1 ${
                    recordingMode === 'melody' 
                      ? 'bg-purple-500 text-white' 
                      : 'bg-stone-700 text-stone-400 hover:bg-stone-600'
                  }`}
                >
                  <Music className="w-3 h-3" /> Melody
                </button>
              </div>
              <p className="text-[10px] text-stone-500 mt-2 text-center">
                {recordingMode === 'beat' 
                  ? 'Beatbox → Drums (Surdo, Caixa, Tamborim)' 
                  : 'Hum/Sing → Musical Notes'}
              </p>
            </div>
            
            {/* Metronome */}
            <div className="bg-stone-900 rounded-xl border border-stone-800 p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold text-stone-400 uppercase tracking-wider flex items-center gap-1">
                  <Activity className="w-3 h-3" /> Metronome
                </span>
                <button
                  onClick={() => setMetronomeEnabled(!metronomeEnabled)}
                  className={`px-2 py-0.5 rounded text-xs font-bold ${metronomeEnabled ? 'bg-amber-500 text-black' : 'bg-stone-700'}`}
                >
                  {metronomeEnabled ? 'ON' : 'OFF'}
                </button>
              </div>
              
              {metronomeEnabled && (
                <div className="flex justify-center gap-1">
                  {[0, 1, 2, 3].map((beat) => (
                    <div
                      key={beat}
                      className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                        (isCountingIn || isRecording) && metronomeCount === beat
                          ? beat === 0 ? 'bg-amber-500 text-black scale-110' : 'bg-amber-500/50 scale-105'
                          : 'bg-stone-700'
                      }`}
                    >
                      {beat + 1}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          {/* Track Waveforms - Right Side */}
          <div className="col-span-9 space-y-3">
            {/* Timeline Header with Mode and Status */}
            <div className="bg-stone-900 rounded-xl border border-stone-800 p-2 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <span className="text-sm text-stone-400">
                  Position: <span className="font-mono text-white">{formatTime(playbackPosition)}</span>
                </span>
                <span className="text-sm text-stone-400">
                  Duration: <span className="font-mono text-white">{formatTime(maxTrackDuration)}</span>
                </span>
                {/* Current Mode Indicator */}
                <span className={`text-xs px-2 py-0.5 rounded flex items-center gap-1 font-medium ${
                  recordingMode === 'beat' 
                    ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' 
                    : 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
                }`}>
                  {recordingMode === 'beat' ? (
                    <><Drum className="w-3 h-3" /> Beat Mode</>
                  ) : (
                    <><Music className="w-3 h-3" /> Melody Mode</>
                  )}
                </span>
                {/* Samples Status - only show for beat mode */}
                {recordingMode === 'beat' && (
                  <span className={`text-xs px-2 py-0.5 rounded flex items-center gap-1 ${
                    samplesLoaded ? 'bg-green-500/20 text-green-400' : 
                    loadingSamples ? 'bg-amber-500/20 text-amber-400' : 
                    'bg-stone-700 text-stone-400'
                  }`}>
                    {loadingSamples ? (
                      <><Loader2 className="w-3 h-3 animate-spin" /> Loading...</>
                    ) : samplesLoaded ? (
                      <><CheckCircle className="w-3 h-3" /> Samples OK</>
                    ) : (
                      <><XCircle className="w-3 h-3" /> Click to load</>
                    )}
                  </span>
                )}
                {/* Melody status - show for melody mode */}
                {recordingMode === 'melody' && (
                  <span className="text-xs px-2 py-0.5 rounded bg-purple-500/10 text-purple-400 flex items-center gap-1">
                    <Sparkles className="w-3 h-3" /> Pitch Detection Ready
                  </span>
                )}
              </div>
              
              <div className="flex items-center gap-2">
                <span className="text-sm text-stone-400">BPM:</span>
                <input
                  type="number"
                  min="60"
                  max="180"
                  value={bpm}
                  onChange={(e) => setBpm(Number(e.target.value))}
                  className="w-16 bg-stone-800 border border-stone-700 rounded px-2 py-1 text-sm text-center"
                />
              </div>
            </div>
            
            {/* Track Lanes */}
            <div className="space-y-2">
              {tracks.map((track, index) => {
                const color = TRACK_COLORS[index % TRACK_COLORS.length];
                const isSelected = selectedTrackId === track.id;
                const isActive = activeTrackId === track.id;
                
                return (
                  <div
                    key={track.id}
                    onClick={() => setSelectedTrackId(track.id)}
                    className={`bg-stone-900 rounded-xl border ${color.border} ${isSelected ? 'ring-2 ring-white/20' : ''} 
                      ${isActive ? 'ring-2 ring-red-500' : ''} overflow-hidden cursor-pointer transition-all`}
                  >
                    {/* Track Header */}
                    <div className={`${color.bg} px-3 py-2 flex items-center justify-between border-b ${color.border}`}>
                      <div className="flex items-center gap-2">
                        <Drum className="w-4 h-4" style={{ color: color.wave }} />
                        <input
                          type="text"
                          value={track.name}
                          onChange={(e) => updateTrack(track.id, { name: e.target.value })}
                          onClick={(e) => e.stopPropagation()}
                          className="bg-transparent text-sm font-medium focus:outline-none focus:ring-1 focus:ring-white/30 rounded px-1"
                        />
                        {track.isConverted && (
                          <span className={`px-2 py-0.5 text-xs rounded-full ${
                            track.mode === 'melody' 
                              ? 'bg-purple-500/20 text-purple-400' 
                              : 'bg-green-500/20 text-green-400'
                          }`}>
                            {track.mode === 'melody' ? '✓ Notes' : '✓ Drums'}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-xs">
                        {/* Convert Button - adapts to track mode */}
                        {track.audioBlob && !track.isConverted && (
                          <button
                            onClick={(e) => { e.stopPropagation(); convertToMidi(track.id); }}
                            disabled={isConverting || (track.mode !== 'melody' && !samplesLoaded)}
                            className={`px-2 py-1 text-xs font-bold flex items-center gap-1 rounded disabled:opacity-50 ${
                              track.mode === 'melody'
                                ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-400 hover:to-pink-400'
                                : 'bg-gradient-to-r from-amber-500 to-orange-500 text-black hover:from-amber-400 hover:to-orange-400'
                            }`}
                          >
                            {isConverting ? (
                              <><Loader2 className="w-3 h-3 animate-spin" /> Converting...</>
                            ) : track.mode === 'melody' ? (
                              <><Sparkles className="w-3 h-3" /> Detect Notes</>
                            ) : (
                              <><Wand2 className="w-3 h-3" /> Convert to Drums</>
                            )}
                          </button>
                        )}
                        {track.muted && <span className="text-red-400">M</span>}
                        {track.solo && <span className="text-yellow-400">S</span>}
                        {isActive && <span className="text-red-500 animate-pulse">● REC</span>}
                      </div>
                    </div>
                    
                    {/* Waveform or MIDI Pattern Display */}
                    <div className="relative h-24 bg-stone-950">
                      {track.isConverted && track.mode === 'melody' && track.midiEvents?.length > 0 ? (
                        // Melody: Piano roll style visualization
                        <div className="h-full p-2 relative overflow-hidden">
                          {/* Piano roll grid */}
                          <div className="absolute inset-0 flex flex-col">
                            {[...Array(12)].map((_, i) => (
                              <div key={i} className={`flex-1 border-b ${i === 0 ? 'border-stone-600' : 'border-stone-800/50'}`} />
                            ))}
                          </div>
                          {/* Note bars */}
                          <div className="relative h-full">
                            {track.midiEvents.map((note, noteIndex) => {
                              const startPercent = (note.time / (track.duration || 8)) * 100;
                              const widthPercent = Math.max(1, (note.duration / (track.duration || 8)) * 100);
                              const pitchOffset = note.pitch % 12; // Note within octave
                              const topPercent = ((11 - pitchOffset) / 12) * 100;
                              
                              return (
                                <div
                                  key={noteIndex}
                                  className="absolute h-[8%] bg-purple-500 rounded-sm opacity-80 hover:opacity-100"
                                  style={{
                                    left: `${startPercent}%`,
                                    width: `${widthPercent}%`,
                                    top: `${topPercent}%`,
                                  }}
                                  title={`${note.note} (vel: ${note.velocity})`}
                                />
                              );
                            })}
                          </div>
                          {/* Note count */}
                          <div className="absolute bottom-1 right-2 text-[10px] text-purple-400">
                            {track.midiEvents.length} notes
                          </div>
                        </div>
                      ) : track.isConverted && track.pattern?.length > 0 ? (
                        // Beat mode: Step sequencer style visualization
                        <div className="h-full p-2">
                          <div className="h-full flex items-end gap-0.5">
                            {track.pattern.map((step, stepIndex) => (
                              <div key={stepIndex} className="flex-1 flex flex-col justify-end gap-0.5 h-full">
                                {step.length > 0 ? (
                                  step.map((hit, hitIndex) => (
                                    <div
                                      key={hitIndex}
                                      className={`h-4 rounded-sm ${INSTRUMENT_COLORS[hit.instrument]?.bg || 'bg-gray-500'}`}
                                      style={{ opacity: hit.velocity / 127 }}
                                      title={`${hit.instrument} (vel: ${hit.velocity})`}
                                    />
                                  ))
                                ) : (
                                  <div className="h-1 bg-stone-800 rounded-sm" />
                                )}
                              </div>
                            ))}
                          </div>
                          {/* Step markers */}
                          <div className="absolute bottom-0 left-0 right-0 flex">
                            {Array(16).fill(0).map((_, i) => (
                              <div key={i} className={`flex-1 h-0.5 ${i % 4 === 0 ? 'bg-stone-600' : 'bg-stone-800'}`} />
                            ))}
                          </div>
                        </div>
                      ) : track.audioBlob ? (
                        <>
                          <canvas
                            ref={(el) => {
                              waveformCanvasRefs.current[track.id] = el;
                              if (el && track.waveformData) {
                                drawWaveform(track.id, track.waveformData);
                              }
                            }}
                            width={800}
                            height={96}
                            className="w-full h-full"
                          />
                          
                          {/* Beat Grid Overlay */}
                          {track.duration > 0 && (
                            <div className="absolute inset-0 pointer-events-none">
                              {/* Calculate beats based on BPM and duration */}
                              {(() => {
                                const secondsPerBeat = 60 / bpm;
                                const totalBeats = Math.ceil(track.duration / secondsPerBeat);
                                const gridLines = [];
                                
                                for (let i = 0; i <= totalBeats; i++) {
                                  const beatTime = i * secondsPerBeat;
                                  const position = (beatTime / track.duration) * 100;
                                  if (position > 100) break;
                                  
                                  const isMeasureStart = i % 4 === 0;
                                  const beatInMeasure = (i % 4) + 1;
                                  
                                  gridLines.push(
                                    <div
                                      key={i}
                                      className="absolute top-0 bottom-0 flex flex-col"
                                      style={{ left: `${position}%` }}
                                    >
                                      {/* Vertical grid line */}
                                      <div 
                                        className={`h-full ${isMeasureStart ? 'w-0.5 bg-amber-500/40' : 'w-px bg-stone-600/30'}`} 
                                      />
                                      {/* Beat number label at top */}
                                      <div 
                                        className={`absolute -top-0.5 -translate-x-1/2 text-[9px] font-mono ${
                                          isMeasureStart ? 'text-amber-400/80 font-bold' : 'text-stone-500/60'
                                        }`}
                                      >
                                        {isMeasureStart ? `${Math.floor(i / 4) + 1}.1` : beatInMeasure}
                                      </div>
                                    </div>
                                  );
                                }
                                return gridLines;
                              })()}
                              
                              {/* Subdivision lines (16th notes) - lighter */}
                              {(() => {
                                const secondsPerBeat = 60 / bpm;
                                const secondsPerSixteenth = secondsPerBeat / 4;
                                const totalSixteenths = Math.ceil(track.duration / secondsPerSixteenth);
                                const subLines = [];
                                
                                for (let i = 0; i < totalSixteenths; i++) {
                                  // Skip main beat positions (every 4th sixteenth)
                                  if (i % 4 === 0) continue;
                                  
                                  const time = i * secondsPerSixteenth;
                                  const position = (time / track.duration) * 100;
                                  if (position > 100) break;
                                  
                                  subLines.push(
                                    <div
                                      key={`sub-${i}`}
                                      className="absolute top-0 bottom-0 w-px bg-stone-700/20"
                                      style={{ left: `${position}%` }}
                                    />
                                  );
                                }
                                return subLines;
                              })()}
                            </div>
                          )}
                          
                          {/* Playhead */}
                          {track.duration > 0 && (
                            <div
                              className="absolute top-0 bottom-0 w-0.5 bg-white/80 pointer-events-none z-10"
                              style={{ left: `${(playbackPosition / track.duration) * 100}%` }}
                            />
                          )}
                        </>
                      ) : (
                        <div className="flex items-center justify-center h-full text-stone-600 relative">
                          {/* Empty track beat grid preview - show 8 seconds worth */}
                          <div className="absolute inset-0 pointer-events-none">
                            {(() => {
                              const previewDuration = 8; // 8 seconds preview
                              const secondsPerBeat = 60 / bpm;
                              const totalBeats = Math.ceil(previewDuration / secondsPerBeat);
                              const gridLines = [];
                              
                              for (let i = 0; i <= totalBeats; i++) {
                                const beatTime = i * secondsPerBeat;
                                const position = (beatTime / previewDuration) * 100;
                                if (position > 100) break;
                                
                                const isMeasureStart = i % 4 === 0;
                                const beatInMeasure = (i % 4) + 1;
                                
                                gridLines.push(
                                  <div
                                    key={i}
                                    className="absolute top-0 bottom-0 flex flex-col"
                                    style={{ left: `${position}%` }}
                                  >
                                    <div 
                                      className={`h-full ${isMeasureStart ? 'w-0.5 bg-stone-700/40' : 'w-px bg-stone-800/40'}`} 
                                    />
                                    <div 
                                      className={`absolute -top-0.5 -translate-x-1/2 text-[9px] font-mono ${
                                        isMeasureStart ? 'text-stone-500/60 font-bold' : 'text-stone-600/40'
                                      }`}
                                    >
                                      {isMeasureStart ? `${Math.floor(i / 4) + 1}.1` : beatInMeasure}
                                    </div>
                                  </div>
                                );
                              }
                              return gridLines;
                            })()}
                          </div>
                          <div className="text-center z-10">
                            <AudioWaveform className="w-8 h-8 mx-auto mb-2 opacity-50" />
                            <p className="text-sm">Click Record to add audio</p>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* MIDI Events List (collapsed by default) */}
                    {track.isConverted && track.midiEvents?.length > 0 && (
                      <div className="px-3 py-2 bg-stone-900/50 border-t border-stone-800 flex items-center gap-2 flex-wrap">
                        <span className="text-xs text-stone-500">{track.midiEvents.length} hits:</span>
                        {Object.entries(
                          track.midiEvents.reduce((acc, e) => {
                            acc[e.instrument] = (acc[e.instrument] || 0) + 1;
                            return acc;
                          }, {})
                        ).map(([inst, count]) => (
                          <span 
                            key={inst}
                            className={`text-xs px-1.5 py-0.5 rounded ${INSTRUMENT_COLORS[inst]?.bg || 'bg-gray-500'} ${INSTRUMENT_COLORS[inst]?.text || 'text-white'} bg-opacity-30`}
                          >
                            {inst}: {count}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            
            {/* Transport Controls */}
            <div className="bg-stone-900 rounded-xl border border-stone-800 p-4">
              {/* Main Transport Buttons */}
              <div className="flex items-center justify-center gap-3 mb-4">
                {/* Rewind */}
                <div className="flex flex-col items-center">
                  <button
                    onClick={rewind}
                    className="p-3 bg-stone-800 hover:bg-stone-700 rounded-full transition-all"
                    title="Rewind to Start"
                  >
                    <SkipBack className="w-5 h-5" />
                  </button>
                  <span className="text-xs text-stone-500 mt-1">Rewind</span>
                </div>
                
                {/* Stop - Now stops BOTH playback AND recording */}
                <div className="flex flex-col items-center">
                  <button
                    onClick={() => {
                      if (isRecording) stopRecording();
                      if (isPlaying) stopPlayback();
                    }}
                    disabled={!isPlaying && !isRecording}
                    className="p-3 bg-stone-800 hover:bg-stone-700 rounded-full transition-all disabled:opacity-40"
                    title="Stop"
                  >
                    <CircleStop className="w-5 h-5" />
                  </button>
                  <span className="text-xs text-stone-500 mt-1">Stop</span>
                </div>
                
                {/* Play/Pause */}
                <div className="flex flex-col items-center">
                  <button
                    onClick={playAllTracks}
                    disabled={!tracks.some(t => t.audioBlob)}
                    className={`p-4 rounded-full transition-all ${
                      isPlaying 
                        ? 'bg-amber-500 text-black' 
                        : 'bg-gradient-to-br from-amber-500 to-orange-600 hover:from-amber-400 hover:to-orange-500'
                    } disabled:opacity-40 disabled:cursor-not-allowed`}
                    title={isPlaying ? "Pause" : "Play All"}
                  >
                    {isPlaying ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6" />}
                  </button>
                  <span className="text-xs text-stone-500 mt-1">{isPlaying ? 'Pause' : 'Play'}</span>
                </div>
                
                {/* Record */}
                <div className="flex flex-col items-center">
                  <button
                    onClick={startWithCountIn}
                    disabled={isProcessing || !selectedTrackId || isRecording}
                    className={`p-4 rounded-full transition-all ${
                      isRecording 
                        ? 'bg-red-600 animate-pulse shadow-lg shadow-red-500/50' 
                        : isCountingIn
                          ? 'bg-amber-500 animate-pulse'
                          : 'bg-stone-800 hover:bg-red-600/50'
                    } disabled:opacity-40`}
                    title="Record"
                  >
                    <Circle className={`w-6 h-6 ${isRecording ? 'fill-white text-white' : 'fill-red-500 text-red-500'}`} />
                  </button>
                  <span className="text-xs text-stone-500 mt-1">Record</span>
                </div>
                
                {/* Fast Forward */}
                <div className="flex flex-col items-center">
                  <button
                    onClick={() => {
                      const maxDur = Math.max(...tracks.map(t => t.duration || 0));
                      setPlaybackPosition(prev => Math.min(maxDur, prev + 2));
                    }}
                    disabled={!tracks.some(t => t.audioBlob)}
                    className="p-3 bg-stone-800 hover:bg-stone-700 rounded-full transition-all disabled:opacity-40"
                    title="Fast Forward"
                  >
                    <SkipForward className="w-5 h-5" />
                  </button>
                  <span className="text-xs text-stone-500 mt-1">Forward</span>
                </div>
              </div>
              
              {/* Divider */}
              <div className="border-t border-stone-700 my-4"></div>
              
              {/* BPM and Quantize Controls */}
              <div className="flex items-center justify-center gap-8">
                {/* BPM Control */}
                <div className="text-center">
                  <div className="text-xs text-stone-500 uppercase tracking-wider mb-2">Tempo (BPM)</div>
                  <div className="flex items-center gap-2">
                    <button 
                      onClick={() => setBpm(prev => Math.max(60, prev - 5))}
                      className="w-8 h-8 rounded bg-stone-800 hover:bg-stone-700 flex items-center justify-center text-lg font-bold"
                    >
                      −
                    </button>
                    <div className="bg-stone-950 border border-stone-700 rounded-lg px-3 py-1 min-w-[60px] text-center">
                      <span className="text-xl font-bold text-amber-400">{bpm}</span>
                    </div>
                    <button 
                      onClick={() => setBpm(prev => Math.min(200, prev + 5))}
                      className="w-8 h-8 rounded bg-stone-800 hover:bg-stone-700 flex items-center justify-center text-lg font-bold"
                    >
                      +
                    </button>
                  </div>
                  <input
                    type="range"
                    min="60"
                    max="200"
                    value={bpm}
                    onChange={(e) => setBpm(Number(e.target.value))}
                    className="w-32 accent-amber-500 mt-2"
                  />
                </div>

                {/* Quantize Control */}
                <div className="text-center border-l border-stone-700 pl-8">
                  <div className="text-xs text-stone-500 uppercase tracking-wider mb-2 flex items-center justify-center gap-1">
                    <Grid3X3 className="w-3 h-3" /> Quantize
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setQuantizeEnabled(!quantizeEnabled)}
                      className={`px-3 py-2 rounded-lg text-sm font-bold transition-all ${
                        quantizeEnabled 
                          ? 'bg-purple-500 text-white' 
                          : 'bg-stone-800 text-stone-400'
                      }`}
                    >
                      {quantizeEnabled ? 'ON' : 'OFF'}
                    </button>
                    {quantizeEnabled && (
                      <select
                        value={gridSize}
                        onChange={(e) => setGridSize(Number(e.target.value))}
                        className="bg-stone-800 border border-stone-700 rounded-lg px-2 py-2 text-sm"
                      >
                        <option value={4}>1/4</option>
                        <option value={8}>1/8</option>
                        <option value={16}>1/16</option>
                        <option value={32}>1/32</option>
                      </select>
                    )}
                  </div>
                </div>

                {/* Status Indicator */}
                <div className="text-center border-l border-stone-700 pl-8">
                  <div className="text-xs text-stone-500 uppercase tracking-wider mb-2">Status</div>
                  <div className={`px-4 py-2 rounded-lg text-sm font-medium ${
                    isCountingIn ? 'bg-amber-600/20 text-amber-400' :
                    isRecording ? 'bg-red-600/20 text-red-400' :
                    isPlaying ? 'bg-green-600/20 text-green-400' :
                    tracks.some(t => t.audioBlob) ? 'bg-amber-600/20 text-amber-400' :
                    'bg-stone-800 text-stone-500'
                  }`}>
                    {isCountingIn ? '● Count-in' :
                     isRecording ? '● Recording...' :
                     isPlaying ? '▶ Playing' :
                     tracks.some(t => t.audioBlob) ? '■ Ready' :
                     '○ Empty'}
                  </div>
                </div>
              </div>
              
              {/* Recording Status */}
              {isRecording && (
                <div className="text-center mt-4 p-2 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <span className="text-red-400 font-mono">
                    Recording to {tracks.find(t => t.id === activeTrackId)?.name}: {formatTime(recordingTime)} / 0:30.0
                  </span>
                  <p className="text-xs text-stone-500 mt-1">Press Stop or wait for auto-stop at 30 seconds</p>
                </div>
              )}
              
              {isCountingIn && (
                <div className="text-center mt-4">
                  <span className="text-amber-400 font-bold animate-pulse text-xl">
                    Count-in: {metronomeCount + 1}
                  </span>
                </div>
              )}
              
              {/* Instructions */}
              {!isRecording && !isPlaying && !isCountingIn && (
                <div className="text-center mt-4 text-sm text-stone-500">
                  {selectedTrackId 
                    ? `Selected: ${tracks.find(t => t.id === selectedTrackId)?.name} • Press Record to start`
                    : 'Select a track to record'}
                </div>
              )}
              
              {/* Keyboard Shortcuts */}
              <div className="mt-4 pt-3 border-t border-stone-800">
                <details className="text-xs text-stone-500">
                  <summary className="cursor-pointer hover:text-stone-300 text-center">⌨️ Keyboard Shortcuts</summary>
                  <div className="mt-2 flex flex-wrap justify-center gap-x-4 gap-y-1">
                    <span><kbd className="px-1.5 py-0.5 bg-stone-800 rounded text-stone-400">Space</kbd> Play/Stop</span>
                    <span><kbd className="px-1.5 py-0.5 bg-stone-800 rounded text-stone-400">R</kbd> Record</span>
                    <span><kbd className="px-1.5 py-0.5 bg-stone-800 rounded text-stone-400">S</kbd> Stop</span>
                    <span><kbd className="px-1.5 py-0.5 bg-stone-800 rounded text-stone-400">Home</kbd> Rewind</span>
                    <span><kbd className="px-1.5 py-0.5 bg-stone-800 rounded text-stone-400">←</kbd><kbd className="px-1.5 py-0.5 bg-stone-800 rounded text-stone-400">→</kbd> Seek</span>
                  </div>
                </details>
              </div>
            </div>
            
            {/* AI Enhancement Section */}
            {replicateKey && tracks.some(t => t.audioBlob) && (
              <div className="bg-gradient-to-r from-purple-900/30 to-pink-900/30 rounded-xl border border-purple-500/30 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-5 h-5 text-purple-400" />
                  <span className="font-bold">AI Enhancement Available</span>
                </div>
                <p className="text-sm text-stone-400">
                  Your Replicate API key is connected. AI music generation from your recordings coming soon!
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-stone-900 rounded-xl w-full max-w-md p-6">
            <h3 className="text-xl font-bold mb-4 flex items-center gap-2"><Settings className="w-5 h-5" /> Settings</h3>
            
            <div className="space-y-4">
              <div className="p-4 bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-lg">
                <h4 className="font-bold mb-2 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-purple-400" />
                  AI Music Generation (Optional)
                </h4>
                <p className="text-xs text-stone-400 mb-3">
                  Connect your Replicate API key for AI-powered music generation.
                </p>
                
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm text-stone-400 mb-1 flex items-center gap-2">
                      <Key className="w-3 h-3" /> Replicate API Key
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="password"
                        value={replicateKey}
                        onChange={(e) => { setReplicateKey(e.target.value); setIsKeyValid(null); }}
                        placeholder="r8_xxxxxxxxxxxxxxxx"
                        className="flex-1 bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm font-mono"
                      />
                      {isKeyValid !== null && (
                        <div className={`p-2 rounded-lg ${isKeyValid ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
                          {isKeyValid ? <CheckCircle className="w-5 h-5 text-green-400" /> : <XCircle className="w-5 h-5 text-red-400" />}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <button
                      onClick={() => validateReplicateKey(replicateKey)}
                      disabled={isValidatingKey}
                      className="flex-1 py-2 bg-purple-600 hover:bg-purple-500 rounded-lg text-sm font-bold disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {isValidatingKey ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                      {isValidatingKey ? 'Validating...' : 'Save & Validate'}
                    </button>
                    <a
                      href="https://replicate.com/account/api-tokens"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-4 py-2 bg-stone-700 hover:bg-stone-600 rounded-lg text-sm flex items-center gap-1"
                    >
                      Get Key <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </div>
              </div>

              <details className="text-sm text-stone-400">
                <summary className="cursor-pointer hover:text-white">How to get a Replicate API key</summary>
                <ol className="mt-2 ml-4 space-y-1 list-decimal">
                  <li>Go to <a href="https://replicate.com" target="_blank" rel="noopener noreferrer" className="text-purple-400 underline">replicate.com</a></li>
                  <li>Sign up (free $5 credits)</li>
                  <li>Go to Account → API Tokens</li>
                  <li>Create and paste token above</li>
                </ol>
              </details>
            </div>
            
            <button onClick={() => setShowSettings(false)} className="w-full mt-4 py-2 bg-stone-700 rounded-lg font-bold hover:bg-stone-600">Close</button>
          </div>
        </div>
      )}

      {/* Community Modal */}
      {showCommunityBeats && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-stone-900 rounded-xl w-full max-w-lg p-6 max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold flex items-center gap-2"><Users className="w-5 h-5 text-amber-400" /> Community</h3>
              <button onClick={() => setShowCommunityBeats(false)} className="p-2 hover:bg-stone-800 rounded-lg"><Square className="w-4 h-4" /></button>
            </div>
            <div className="flex-1 overflow-y-auto space-y-2">
              {loadingBeats ? (
                <div className="text-center py-8"><Loader2 className="w-8 h-8 animate-spin mx-auto" /></div>
              ) : communityBeats.length === 0 ? (
                <div className="text-center py-8 text-stone-500">
                  <Music className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No community tracks yet</p>
                </div>
              ) : communityBeats.map(beat => (
                <div key={beat.id} className="p-4 bg-stone-800 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-bold">{beat.title}</h4>
                      <p className="text-sm text-stone-400">by {beat.creator_name}</p>
                    </div>
                    <div className="text-xs text-stone-500 flex items-center gap-1">
                      <TrendingUp className="w-3 h-3" />{beat.plays} plays
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
